import CookieService from './CookieService';
import QLogger from './QLogger';
import { isProd } from '../utils/EnvUtils';
import UserService from './UserService';
import { isCountryUS } from '~/utils/ExperimentUtils';
import IdleQueueService from './IdleQueueService';

function getSignupType(user) {
  if (user?.userStore?.microsoft) {
    return 'microsoft';
  } if (user?.userStore?.google) {
    return 'google';
  } if (user?.userStore?.local?.phoneNumber) {
    return 'mobile';
  } if (user?.local?.email) {
    return 'email';
  }
  return '';
}

const FC_LIST = ['CA', 'GB', 'AU'];
const newHotJarCountries = [...FC_LIST];

export default class HotjarService {
  static init() {
    if (window.hj || !isProd()) {
      return;
    }

    function hotjar(h, o, t, j, a, r) {
      // eslint-disable-next-line prefer-rest-params
      h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
      h._hjSettings = { hjid: HotjarService.getTrackingId(), hjsv: 6 };
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script'); r.defer = true;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    }

    hotjar(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
    if (this.userData) {
      this.setUserAttributes(this.userData);
    }
  }

  static getTrackingId() {
    // Different project for QFW users
    if (window.serverData?.user?.occupation?.includes('corporate')) {
      return 3866945;
    }

    if (newHotJarCountries.includes(window.serverData?.requestCountry)) {
      return 3849542;
    }

    const userService = new UserService(window?.serverData?.user);

    if (isCountryUS() && userService.isSndUser) {
      return 3897380;
    }

    return 2931244;
  }

  static userData = null;
  static setUserAttributes(user) {
    if (!user.id) {
      return;
    }
    this.userData = user;
    if (user.id && window.hj) {
      const signupType = getSignupType(user);

      window.hj('identify', user?.id, {
        user_id: user?.id,
        occupation: user?.occupation,
        country: user?.country,
        plan: user?.currentPlanStatus?.status,
        createdAt: user?.createdAt,
        organizationId: user?.organization,
        studentUnderage: user?.userStore?.student?.underage,
        paidOrganization: user?.paidOrganization,
        organizationRole: user?.organizationRole,
        experiment: CookieService.get('QUIZIZZ_EXP_NAME'),
        signupType,
      });
    }
  }

  static triggerEvent(eventName) {
    IdleQueueService.addTask(() => {
      this.init();
      QLogger.log('\n HotjarService logEvent:', eventName); // use hjDebug=1 as query param to turn on debugger mode
      if (window.hj) {
        window.hj('event', eventName);
      }
    });
  }
}

// function getHotjarEventWithExperiment(event) {
//   return `${event}_${CookieService.get('QUIZIZZ_EXP_NAME')}`;
// }

export const HotjarEvents = {
  // Hotjar Events

  // The final event name will be '<event_name>_<experiment_name>'
  // example - admin_dashboard_visit_main_mai

  PRICING_PG_V2_VISITED: 'PRICING_PG_V2_VISITED',
  QFW_SAVE_QUIZ: 'qfw_save_quiz',
  HOME_WITH_KURIKULUM: 'home_with_kurikulum',
  DEMO_QUIZ_PAGE: 'demo_quiz_page',

  SETTINGS_PAGE_EVALUATE: 'settings_page_evaluate',
  SETTINGS_PAGE_CONTINUE: 'settings_page_continue',
  NEW_GAME_MODE_SELECTION: 'new_game_mode_selection',

  // Qfw Hotjar Events
  QFW_AI_QUIZ_GEN: 'qfw_ai_quiz_gen',
  QFW_PRICING_PAGE_SHOWN: 'qfw_pricing_page_shown',
  QFW_AI_STUDIO: 'qfw_ai_studio',
  QFW_COURSES_PAGE_LOAD: 'qfw_courses_page_load',
  QFW_LESSON_EDITOR_LOAD: 'qfw_lesson_editor_load',
  QFW_LIBRARY: 'qfw_library',

  QUIZIZZ_AI_QUIZ_GEN: 'quizizz_ai_quiz_gen',
  QUIZIZZ_AI_CREATE_MODAL_OPEN: 'quizizz_ai_create_modal_open',
  QUIZIZZ_AI_QUIZ_GEN_BTN_CLICK: 'quizizz_ai_quiz_gen_btn_click',
  QUIZIZZ_AI_ENHANCE_PANEL_OPEN: 'quizizz_ai_enhance_panel_open',
  QUIZIZZ_AI_LESSON_GEN_IN_PROGRESS: 'quizizz_ai_lesson_gen_in_progress',
  QUIZIZZ_AI_QUIZ_GEN_STREAMING_STARTED: 'quizizz_ai_quiz_gen_streaming_started',
  QUIZIZZ_AI_SEARCH_BYOC_GEN: 'quizizz_ai_search_byoc_gen',
  QUIZIZZ_AI_CREATE_V2: 'quizizz_ai_create_v2',
  QUIZIZZ_AI_PAUSE_STREAMING: 'quizizz_ai_pause_streaming',
  QUIZIZZ_AI_RECOMMENDATIONS_VISIBLE: 'quizizz_ai_recommendations_visible',
  QUIZIZZ_AI_PREMIUM_RECOMMENDATIONS: 'quizizz_ai_premium_recommendations',
  QUIZIZZ_AI_RECOMMENDATIONS_HEADER_VISIBLE: 'quizizz_ai_recommendations_header_visible',
  QUIZIZZ_AI_QUESTION_ADDED_FROM_RECOMMENDATIONS: 'quizizz_ai_question_added_from_recommendations',
  QUIZIZZ_AI_DOK_LEVEL_SELECTED: 'quizizz_ai_dok_level_selected',

  NEW_USER_CREATE_QUESTION: 'new_user_create_question',
  REPORTS_LISTING_PAGE: 'reports_listing_page',

  CATEGORIZE_QUESTION_EDITOR: 'categorize_question_editor',
  AI_CREATE_SEARCH_INTENT: 'ai_create_search_intent',

  // Editor events
  COMPREHENSION_EDITOR_LOAD: 'comprehension_editor_load',
  COMPREHENSION_VIDEO_EDITOR_LOAD: 'comprehension_video_editor_load',
  NEW_EDITOR_WITH_LEFT_NAV: 'new_editor_with_left_nav',
  NEW_EDITOR_WITH_LEFT_NAV_REVIEW: 'new_editor_with_left_nav_review',

  // AI Enhance events
  AI_ENHANCE_QUIZ_PAGE_OPEN: 'ai_enhance_quiz_page_open',
  AI_ENHANCE_QUIZ_PAGE_HEADER_OPEN: 'ai_enhance_quiz_page_header_open',
  AI_ENHANCE_EDITOR_TOOLBAR_OPEN: 'ai_enhance_editor_toolbar_open',
  AI_ENHANCE_EDITOR_HEADER_OPEN: 'ai_enhance_editor_header_open',

  VOCAB_LAND_ON_HOME: 'vocab_land_on_home',

  FFS_PAGE: 'ffs_page',

  // New Signup Flow
  SIGNUP_SWAP_FLOW_SHOWN: 'signup_swap_flow_shown',
  LL_OUT_OF_STORAGE_MODAL_SHOWN: 'll_out_of_storage_modal_shown',
  LL_OUT_OF_STORAGE_MY_LIBRARY_VISITED: 'll_out_of_storage_my_library_visited',

  NEW_ADMIN_ONBOARDING: 'new_admin_onboarding',
  NEW_SND_NON_ACTIVATED_USER_VISITED: 'new_snd_non_activated_user_visited',
  NEW_SND_ACTIVATED_USER_VISITED: 'new_snd_activated_user_visited',

  DISCOVERY_LITERATURE_EXPERIENCE: 'discovery_literature_experience',
  DISCOVERY_SKILL_EXPERIENCE: 'discovery_skill_experience',

  // Differentiation Flow
  DIFFERENTIATION_INTRO: 'differentiation_intro_flow',
  DIFFERENTIATION_ACCOMODATIONS_FLOW: 'differentiation_accomodations_flow',
  DIFFERENTIATION_FROM_GAME_SETTINGS: 'differentiation_from_game_settings',
  DIFFERENTIATION_UNIFIED_FLOW: 'differentiation_unified_flow',
  BTS_ONBOARDING_WELCOME_SCREEN_SHOWN: 'bts_onboarding_welcome_screen_shown',
  ANONYMOUS_USER_QUIZ_PAGE_VISITED: 'anonymous_user_quiz_page_visited',

  // Pause and Resume lesson
  RESUME_LESSON: 'resume_lesson',

  // Quiz publish issue
  LESSON_PUBLISH_ISSUE: 'lesson_publish_issue',

  // BYOC
  BYOC_IMPORT_WORKSHEET: 'byoc_import_worksheet',
  BYOC_IMPORT_FORMS: 'byoc_import_forms',

  // GCSE
  GCSE_PAGE: 'gcse_page',
};
